import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Mic } from 'lucide-react';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { useHoldToConfirm } from '../../lib/holdToConfirm';
import { NumberStepper } from '../../components/ui/NumberStepper';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { DosePresetChip } from '../../components/domain/DoseChip';
import { HALTPrompt, type HaltState } from '../../components/domain/HALTPrompt';
import { TriggerChips, type TriggerTag } from '../../components/domain/TriggerChips';
import { HonestPause } from '../../components/domain/HonestPause';
import { BrandAutocomplete, type BrandRow } from '../../components/domain/BrandAutocomplete';
import { COMMON_TABLET_STRENGTHS_MG, MG_7OH_PER_GRAM_LEAF } from '@shared/constants';
import { computeMg7oh } from '@shared/taperAlgorithm';
import type { Dose, TaperPlanWire, UserProduct } from '@shared/types';
import { apiPatch, apiPost } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';
import { haptics } from '../../lib/haptics';
import { localYmd, todayYmd, ymdAddDays } from '../../lib/format';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../auth/AuthContext';

type Form = 'tablet_7oh' | 'leaf' | 'extract';
type DoseIntent = 'pain' | 'stress' | 'routine' | 'withdrawal' | 'unknown';

interface LogDoseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

// Persisted form memory so the dose sheet defaults to the user's last
// choice instead of always Tablet · 10 mg. Local-only (per-device) — we
// never want this to round-trip to the server because it's UX state,
// not a tracked clinical value.
//
// v2 added `ts` so the same-as-last hero card knows whether the prior
// dose is recent enough to suggest a one-tap repeat. Falls back to v1
// gracefully (treated as ts-unknown → no hero card until next save).
const LAST_DOSE_KEY = 'qk.lastDose.v2';
const LAST_DOSE_KEY_LEGACY = 'qk.lastDose.v1';

export interface LastDose {
  form: Form;
  amount: number;
  concentration?: number;
  strain?: string;
  /** ISO timestamp of when this dose was saved. Empty string = unknown
   *  (migrated from v1; the repeat card stays hidden until next save). */
  ts: string;
}

export function readLastDose(): LastDose | null {
  try {
    const raw = window.localStorage.getItem(LAST_DOSE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LastDose;
      if (validLastDose(parsed)) return parsed;
    }
    // Fall back to v1; ts unknown so the repeat-card heuristic skips it.
    const legacy = window.localStorage.getItem(LAST_DOSE_KEY_LEGACY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as Omit<LastDose, 'ts'>;
      if (validLastDose({ ...parsed, ts: '' })) return { ...parsed, ts: '' };
    }
    return null;
  } catch {
    return null;
  }
}

function validLastDose(p: unknown): p is LastDose {
  if (!p || typeof p !== 'object') return false;
  const d = p as LastDose;
  return (
    ['tablet_7oh', 'leaf', 'extract'].includes(d.form) &&
    typeof d.amount === 'number' &&
    Number.isFinite(d.amount)
  );
}

export function writeLastDose(d: LastDose): void {
  try {
    window.localStorage.setItem(LAST_DOSE_KEY, JSON.stringify(d));
  } catch {
    // Quota or privacy mode — best effort.
  }
}

export function LogDoseSheet({ open, onOpenChange, onSaved }: LogDoseSheetProps) {
  const toast = useToast();
  const { me } = useAuth();
  // User-calibrated leaf potency (UserSettings.leafPotencyMgPerGram).
  // Falls back to the literature midpoint (0.4 mg/g) when unset. This
  // is preview-only — server recomputes authoritatively at save time.
  const leafPotency =
    typeof me?.settings.leafPotencyMgPerGram === 'number' &&
    me.settings.leafPotencyMgPerGram > 0
      ? me.settings.leafPotencyMgPerGram
      : MG_7OH_PER_GRAM_LEAF;
  const last = readLastDose();
  const [form, setForm] = useState<Form>(last?.form ?? 'tablet_7oh');
  const [amount, setAmount] = useState(last?.amount ?? 10);
  const [concentration, setConcentration] = useState(last?.concentration ?? 10);
  const [note, setNote] = useState('');
  const [strain, setStrain] = useState(last?.strain ?? '');
  // Powder-tracking sprint (migration 0080). Vendor + batch are
  // structured-but-optional metadata for leaf doses; estimated marks
  // the dose's computed mg as a personal guess rather than label-precise.
  // Defaults: empty / false. Surfacing them as form fields next to
  // strain when form === 'leaf'; estimated is a checkbox at the bottom.
  const [vendor, setVendor] = useState('');
  const [batch, setBatch] = useState('');
  const [estimated, setEstimated] = useState(false);
  // Custom product registry (Gap 2, migration 0081). productId pins
  // this dose to one of the user's registered products; tapping a chip
  // in the quick-pick row sets it (and prefills the form/amount/unit).
  // Manually editing form/amount AFTER picking a product clears the
  // pin so the dose doesn't carry a misleading product reference.
  const [productId, setProductId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState<TriggerTag[]>([]);
  const [haltSent, setHaltSent] = useState(false);
  const [shareSource, setShareSource] = useState<string | null>(null);
  // PR 3B (Gap 6) — optional brand id for tablet_7oh form. When set,
  // the server substitutes the tested band midpoint for the label mg.
  const [brandId, setBrandId] = useState<number | null>(null);
  const [brandRow, setBrandRow] = useState<BrandRow | null>(null);

  // Voice-note state: a small mic button next to the note input that
  // records up to 60s, uploads to /api/voice-note for Whisper
  // transcription, and drops the transcript into the note field for
  // the user to review/edit before saving. Audio is never persisted.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [noteExpanded, setNoteExpanded] = useState(false);
  // Backdate state: null means "now" (resolved at save time so the
  // server records the actual save instant). A Date pins the dose to
  // an explicit moment for users logging retroactively.
  const [doseTs, setDoseTs] = useState<Date | null>(null);
  // Hide the same-as-last hero card for the rest of this open if the
  // user dismissed it. Reset on each new open so they can re-see it
  // tomorrow without manual reset.
  const [repeatDismissed, setRepeatDismissed] = useState(false);
  // Counter for "Save & log another" — caps at 3 in a row to prevent
  // runaway logging from a stuck modal state.
  const [logAnotherCount, setLogAnotherCount] = useState(0);
  // Honest Pause: true when the user waited the full 90s breathing
  // window OR explicitly tapped "I took a breath." Sent to the server
  // as `pausedBeforeSave`. Reset on each open.
  const [paused, setPaused] = useState(false);
  // Pre-dose intent — single-tag "why am I taking this right now?"
  // Captured at log time, sent on save. Optional; null means no
  // declared intent.
  const [intent, setIntent] = useState<DoseIntent | null>(null);
  // Edit-recent: when set, the next Save PATCHes this dose id instead
  // of POSTing a new one. Triggered by the "Edit that one" CTA on the
  // edit-recent banner that appears when the user opens the sheet
  // within 10 minutes of a save with the same form.
  const [editingDoseId, setEditingDoseId] = useState<number | null>(null);
  const [editRecentDismissed, setEditRecentDismissed] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStopRef = useRef<number | null>(null);
  const recordTickRef = useRef<number | null>(null);

  // Reset Phase 6 state every time the sheet opens — these are
  // per-dose facts, not persistent prefs.
  useEffect(() => {
    if (open) {
      setTags([]);
      setHaltSent(false);
      setDoseTs(null);
      setNoteExpanded(false);
      setRepeatDismissed(false);
      setLogAnotherCount(0);
      setPaused(false);
      setIntent(null);
      setEditingDoseId(null);
      setEditRecentDismissed(false);
      // Phase 12 share-target inbox: drain anything routes.tsx parked
      // in sessionStorage and prefill the note + show a small banner.
      try {
        const raw = window.sessionStorage.getItem('qk.shareInbox.v1');
        if (raw) {
          const parsed = JSON.parse(raw) as { title?: string; text?: string; url?: string };
          const parts = [parsed.title, parsed.text, parsed.url].filter(Boolean) as string[];
          if (parts.length > 0) {
            setNote(parts.join(' · ').slice(0, 500));
            let source: string = parsed.title ?? 'shared';
            if (parsed.url) {
              try { source = new URL(parsed.url).hostname; } catch { /* keep title */ }
            }
            setShareSource(source);
          }
          window.sessionStorage.removeItem('qk.shareInbox.v1');
        }
      } catch {
        // ignored — share inbox is best-effort UX, not a critical path
      }
    } else {
      setShareSource(null);
    }
  }, [open]);

  async function startRecording() {
    if (recording || transcribing) return;
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast.push("This browser can't record audio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      recordChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        // Tracks must be released or the mic icon stays on in the OS.
        stream.getTracks().forEach((t) => t.stop());
        void finalizeRecording();
      };
      mr.start();
      setRecording(true);
      setRecordSeconds(0);
      // Drive the live "0:23 / 1:00" countdown next to the mic button.
      // 250ms tick is enough resolution for whole-second display while
      // staying cheap.
      recordTickRef.current = window.setInterval(() => {
        setRecordSeconds((s) => Math.min(60, s + 0.25));
      }, 250);
      // Hard cap at 60s.
      recordStopRef.current = window.setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 60_000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown';
      toast.push(msg.includes('denied') || msg.includes('Permission') ? 'Mic access was denied.' : 'Could not start recording.');
    }
  }

  function stopRecording() {
    if (recordStopRef.current) {
      window.clearTimeout(recordStopRef.current);
      recordStopRef.current = null;
    }
    if (recordTickRef.current) {
      window.clearInterval(recordTickRef.current);
      recordTickRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }

  async function finalizeRecording() {
    setRecording(false);
    const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
    recordChunksRef.current = [];
    mediaRecorderRef.current = null;
    if (blob.size === 0) return;
    setTranscribing(true);
    try {
      const csrf = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/)?.[1] ?? '';
      const res = await fetch('/api/voice-note', {
        method: 'POST',
        headers: {
          'Content-Type': 'audio/webm',
          'X-CSRF-Token': decodeURIComponent(csrf),
        },
        body: blob,
        credentials: 'same-origin',
      });
      if (!res.ok) {
        throw new Error(`http_${res.status}`);
      }
      const json = (await res.json()) as { text?: string };
      const text = (json.text ?? '').trim();
      if (text) {
        // Append rather than overwrite so the user doesn't lose typed context.
        setNote((cur) => (cur ? `${cur} ${text}` : text).slice(0, 500));
        toast.push('Transcribed — review before saving');
      } else {
        toast.push("Couldn't hear anything — try again");
      }
    } catch {
      toast.push("Couldn't transcribe — try again or type the note.");
    } finally {
      setTranscribing(false);
    }
  }

  // Stop any in-flight recording when the sheet closes (e.g. user taps backdrop).
  useEffect(() => {
    if (!open && mediaRecorderRef.current?.state === 'recording') {
      stopRecording();
    }
  }, [open]);

  // Unmount-only cleanup. The `!open` effect above handles backdrop
  // dismissal, but if the component unmounts while recording (route
  // change, parent teardown) nothing tears down the interval/timeout
  // and the mic stays hot — the OS recording indicator gets stuck on.
  // Stop everything directly here: `mr.onstop` is not guaranteed to run
  // synchronously during unmount, so we also stop the getUserMedia
  // tracks ourselves rather than relying on the onstop handler.
  useEffect(() => {
    return () => {
      if (recordStopRef.current) {
        window.clearTimeout(recordStopRef.current);
        recordStopRef.current = null;
      }
      if (recordTickRef.current) {
        window.clearInterval(recordTickRef.current);
        recordTickRef.current = null;
      }
      const mr = mediaRecorderRef.current;
      if (mr) {
        // Release the live audio tracks first so the mic goes cold
        // even if onstop never fires post-unmount.
        try {
          mr.stream.getTracks().forEach((t) => t.stop());
        } catch {
          // stream may already be gone — best effort.
        }
        if (mr.state === 'recording') {
          try {
            mr.stop();
          } catch {
            // recorder already stopped — ignore.
          }
        }
        mediaRecorderRef.current = null;
      }
    };
  }, []);

  async function onHaltChange(state: HaltState) {
    // Fire-once per sheet session — the very first toggle commits a
    // halt_checkins row. Subsequent toggles in the same session refine
    // local UI state but don't re-POST (avoid spam).
    if (haltSent) return;
    if (!state.hungry && !state.angry && !state.lonely && !state.tired) return;
    setHaltSent(true);
    try {
      await apiPost('/api/halt', state);
    } catch (err) {
      if (!isQueuedOffline(err)) {
        // Quiet failure — HALT is opportunistic, not blocking.
        // Resurface the toggle so the user can retry next time they open.
        setHaltSent(false);
      }
    }
  }

  // Re-hydrate from storage every time the sheet opens, so a "same as
  // last dose" save from elsewhere is reflected here too. Product
  // pin is fresh per-open (don't carry a stale pin forward).
  useEffect(() => {
    if (!open) return;
    setProductId(null);
    const v = readLastDose();
    if (v) {
      setForm(v.form);
      setAmount(v.amount);
      if (v.concentration != null) setConcentration(v.concentration);
      if (v.strain != null) setStrain(v.strain);
    }
  }, [open]);

  // Per-form precision: tablets are sold/dosed in whole mg increments, so
  // 0.5 steps add false precision and make the +/- buttons feel imprecise.
  // Leaf grams and extract mL benefit from 0.1 granularity.
  const step = form === 'tablet_7oh' ? 1 : 0.1;
  const unit = form === 'tablet_7oh' ? 'mg' : form === 'leaf' ? 'g' : 'mL';

  // Magnitude anchor: pull the user's last 7 calendar days of doses + the
  // current taper plan, but only while the sheet is open so we don't burn
  // requests when the sheet is unmounted-but-rendered. Refetched on each
  // open — fine for the small payload, and naturally picks up changes
  // from quick-log on TodayScreen between opens.
  const today = todayYmd();
  const sevenDaysAgo = ymdAddDays(today, -6);
  const dosesRes = useApi<Dose[]>(open ? `/api/doses?from=${sevenDaysAgo}&to=${today}` : null);
  const taperRes = useApi<TaperPlanWire | null>(open ? '/api/taper' : null);
  // Smart-default amount (Tier 3.1) — server-side feature-flag gated;
  // returns { smartDefaultMg: null } unless the flag is enabled for
  // this user. We only apply the suggestion if it arrives BEFORE the
  // user touches the stepper (tracked via amountTouchedRef) so a slow
  // network never overrides an in-progress edit.
  const defaultsRes = useApi<{ smartDefaultMg: number | null; source: string | null; enabled: boolean }>(
    open && form === 'tablet_7oh' ? '/api/me/dose-defaults' : null,
  );
  // Custom product registry (Gap 2). Fetched on sheet open; the active
  // products surface as a quick-pick chip row above the form picker.
  // Empty array is the common case for new users; gracefully no-renders.
  const productsRes = useApi<{ products: UserProduct[] }>(
    open ? '/api/me/products' : null,
  );
  const products = productsRes.status === 'ok' ? productsRes.data.products : [];

  function applyProduct(p: UserProduct) {
    setProductId(p.id);
    setForm(p.form);
    setAmount(p.amount);
    if (p.concentrationMgPerMl != null) setConcentration(p.concentrationMgPerMl);
    // Product's `estimated` propagates onto the dose; the user can still
    // toggle the checkbox below to override per-dose if they want.
    setEstimated(p.estimated);
    // Mark amount as touched so the smart-default doesn't clobber the
    // user's intent here.
    amountTouchedRef.current = true;
  }

  function clearProduct() {
    setProductId(null);
  }
  const amountTouchedRef = useRef(false);
  useEffect(() => {
    if (!open) amountTouchedRef.current = false;
  }, [open]);
  useEffect(() => {
    if (defaultsRes.status !== 'ok') return;
    if (amountTouchedRef.current) return;
    const mg = defaultsRes.data.smartDefaultMg;
    if (mg && mg > 0 && form === 'tablet_7oh') {
      setAmount(mg);
    }
  }, [defaultsRes, form]);
  // Wrap setAmount so any user-driven change marks the value as
  // touched, preventing a late-arriving smart default from clobbering
  // it. The stepper, presets, and Same-as-last all flow through here.
  const setAmountUserTouched = useCallback((next: number) => {
    amountTouchedRef.current = true;
    setAmount(next);
  }, []);

  // Live computed mg of 7-OH for the current input. Drives the sticky
  // Save button label and (later) the magnitude-anchor color shift. The
  // server recomputes authoritatively at save time — this is preview only.
  const computedMg = useMemo(() => {
    try {
      return computeMg7oh({
        form,
        amount,
        amountUnit: unit,
        concentrationMgPerMl: form === 'extract' ? concentration : null,
        mgPerGramLeaf: leafPotency,
      });
    } catch {
      return 0;
    }
  }, [form, amount, unit, concentration, leafPotency]);

  // Anchor data: what's "typical" for this user (trailing 6 days, p10–p90)
  // and how much they've already logged today (sum of computedMg7oh on
  // doses with ts in today's *local* YYYY-MM-DD bucket). Comparing the
  // raw UTC ISO `d.ts` against `today` directly inverts the bucket near
  // UTC midnight (a PST user logs at 11pm → UTC says next day → "past"
  // not "today"). localYmd() converts the UTC ts to the user's local
  // YYYY-MM-DD so the bucket matches the user's calendar.
  const anchor = useMemo(() => {
    if (dosesRes.status !== 'ok') return null;
    const all = dosesRes.data;
    if (all.length < 5) return null; // not enough history to anchor
    const todayDoses: number[] = [];
    const pastDoses: number[] = [];
    for (const d of all) {
      if (localYmd(d.ts) === today) todayDoses.push(d.computedMg7oh);
      else pastDoses.push(d.computedMg7oh);
    }
    const todayMg = todayDoses.reduce((sum, mg) => sum + mg, 0);
    const past = pastDoses.sort((a, b) => a - b);
    const typical = past.length >= 3
      ? { lo: percentile(past, 0.1), hi: percentile(past, 0.9) }
      : null;
    const targetMg = taperRes.status === 'ok' && taperRes.data
      ? taperRes.data.days.find((d) => d.date === today)?.targetMg ?? null
      : null;
    return { todayMg, typical, targetMg };
  }, [dosesRes, taperRes, today]);

  // Edit-recent: if the user opens the sheet within 10 minutes of
  // saving a dose of the same form, surface a banner offering to
  // edit that dose instead of logging a new one. Common for users
  // who realize they typo'd 75 mg as 7.5 mg the moment after save.
  const editRecent = useMemo(() => {
    if (dosesRes.status !== 'ok') return null;
    if (editingDoseId != null || editRecentDismissed) return null;
    const newest = dosesRes.data[0]; // server returns ORDER BY ts DESC
    if (!newest) return null;
    if (newest.form !== form) return null;
    const age = Date.now() - new Date(newest.ts).getTime();
    if (age < 0 || age >= 10 * 60_000) return null;
    return newest;
  }, [dosesRes, editingDoseId, editRecentDismissed, form]);

  function startEditRecent() {
    if (!editRecent) return;
    setEditingDoseId(editRecent.id);
    setForm(editRecent.form);
    setAmount(editRecent.amount);
    if (editRecent.concentrationMgPerMl != null) setConcentration(editRecent.concentrationMgPerMl);
    if (editRecent.strain) setStrain(editRecent.strain);
    setNote(editRecent.note ?? '');
    if (editRecent.note) setNoteExpanded(true);
    // Pre-fill the new powder-tracking fields too so an edit doesn't
    // silently blank them. Falsy values (null, '', undefined) skip.
    setVendor(editRecent.vendor ?? '');
    setBatch(editRecent.batch ?? '');
    setEstimated(editRecent.estimated === true);
    // Edits don't backdate — keep doseTs null. Editable subset is
    // amount/conc/strain/note/tags/form/vendor/batch/estimated per the
    // PATCH contract.
  }

  // Smart trigger ordering: pick 3 contextually-likely tags by hour-of-
  // day so the user sees the most relevant chips first when they
  // expand the picker. Pure heuristic (no backend); recomputed on
  // every render but cheap (a single Date + branch).
  const suggestedTriggers = suggestTriggersForHour(new Date().getHours());

  async function persist(closeAfter: boolean) {
    setBusy(true);
    try {
      if (editingDoseId != null) {
        // Edit-recent path: PATCH the existing dose. Server recomputes
        // computed_mg_7oh from the new shape; intent/pausedBeforeSave/
        // ts are atomic-to-the-original and intentionally not editable.
        await apiPatch(`/api/doses/${editingDoseId}`, {
          form,
          amount,
          amountUnit: unit,
          concentrationMgPerMl: form === 'extract' ? concentration : null,
          strain: form === 'leaf' ? (strain || null) : null,
          note: note || null,
          tags: tags.length > 0 ? tags : [],
          vendor: form === 'leaf' ? (vendor || null) : null,
          batch: form === 'leaf' ? (batch || null) : null,
          estimated,
        });
      } else {
        await apiPost('/api/doses', {
          form,
          amount,
          amountUnit: unit,
          concentrationMgPerMl: form === 'extract' ? concentration : undefined,
          strain: form === 'leaf' && strain ? strain : undefined,
          note: note || undefined,
          tags: tags.length > 0 ? tags : undefined,
          // Backdate when the user picked a relative-time pill or custom
          // datetime. Server already accepts optional `ts` (DoseBody.ts
          // is z.string().datetime().optional()) and falls back to now.
          ts: doseTs ? doseTs.toISOString() : undefined,
          // Honest Pause flag — observability only; doesn't gate save.
          pausedBeforeSave: paused || undefined,
          intent: intent ?? undefined,
          // PR 3B (Gap 6) — brand substitution. Only sent when the
          // user picked a brand AND the form is tablet_7oh.
          brandId: form === 'tablet_7oh' && brandId ? brandId : undefined,
          // Powder-tracking sprint. Vendor + batch only carry meaning for
          // leaf; estimated is form-agnostic (true means "this mg is a
          // personal guess, not a label").
          vendor: form === 'leaf' && vendor ? vendor : undefined,
          batch: form === 'leaf' && batch ? batch : undefined,
          estimated: estimated || undefined,
          // Custom product registry (Gap 2). When a quick-pick chip
          // was tapped, the server resolves the product and applies
          // its mg-per-unit / concentration / estimated bits.
          productId: productId ?? undefined,
        });
      }
      writeLastDose({
        form,
        amount,
        concentration: form === 'extract' ? concentration : undefined,
        strain: form === 'leaf' && strain ? strain : undefined,
        ts: (doseTs ?? new Date()).toISOString(),
      });
      haptics.confirm();
      toast.push(
        editingDoseId != null
          ? 'Dose updated'
          : closeAfter
            ? 'Dose logged'
            : 'Logged — ready for the next',
      );
      onSaved?.();
      if (closeAfter) {
        onOpenChange(false);
      } else {
        // Reset transient state for the next dose; keep form/amount/
        // strain/concentration so a routine multi-dose flow stays fast.
        setNote('');
        setTags([]);
        setDoseTs(null);
        setNoteExpanded(false);
        setLogAnotherCount((c) => c + 1);
      }
    } catch (err) {
      if (isQueuedOffline(err)) {
        toast.push("Saved offline — we'll sync when you're back online.");
        if (closeAfter) onOpenChange(false);
      } else {
        toast.push(apiErrorMessage(err, 'Could not log — try again.'));
      }
    } finally {
      setBusy(false);
    }
  }
  const onSave = () => persist(true);
  const onSaveAndAnother = () => persist(false);

  // One-tap repeat from the same-as-last hero card. Posts a clean
  // duplicate of the last dose (no triggers, no note, no backdate)
  // and closes the sheet.
  async function repeatLast(prior: LastDose) {
    setBusy(true);
    try {
      const u: 'mg' | 'g' | 'mL' = prior.form === 'tablet_7oh' ? 'mg' : prior.form === 'leaf' ? 'g' : 'mL';
      await apiPost('/api/doses', {
        form: prior.form,
        amount: prior.amount,
        amountUnit: u,
        concentrationMgPerMl: prior.form === 'extract' ? prior.concentration : undefined,
        strain: prior.form === 'leaf' && prior.strain ? prior.strain : undefined,
      });
      writeLastDose({ ...prior, ts: new Date().toISOString() });
      haptics.confirm();
      toast.push('Same as last — saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      if (isQueuedOffline(err)) {
        toast.push("Saved offline — we'll sync when you're back online.");
        onOpenChange(false);
      } else {
        toast.push(apiErrorMessage(err, 'Could not log — try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Log a dose">
      <div className="form-section">
        {/* Phase 3 #15 — gate the 90s Honest-Pause window on the user
            having a prior logged dose. The pause is a beat-of-
            intentionality for routine logging, not a barrier for the
            day-1 user logging their first dose. Once the user has any
            saved dose (last !== null) the pause re-engages for all
            subsequent logs. The flag-check is reactive to `last`
            because readLastDose() is called per-render — when the
            sheet reopens after the first save, lastDose !== null and
            the pause shows. */}
        {last != null ? (
          <HonestPause open={open} onPaused={() => setPaused(true)} />
        ) : null}
        <TimestampPill value={doseTs} onChange={setDoseTs} />
        {editRecent ? (
          <EditRecentBanner
            doseTs={editRecent.ts}
            amount={editRecent.amount}
            unit={editRecent.amountUnit}
            onEdit={startEditRecent}
            onDismiss={() => setEditRecentDismissed(true)}
          />
        ) : null}
        {editingDoseId != null ? (
          <p className="text-caption" style={{ margin: 0, color: 'var(--color-warning, var(--color-ink-secondary))' }}>
            Editing the {editRecent ? '' : 'recent '}dose — Save will UPDATE it, not log a new one.
          </p>
        ) : null}
        {last && last.ts && !repeatDismissed && isRecent(last.ts, 12) && logAnotherCount === 0 && !editRecent && editingDoseId == null ? (
          <RepeatLastDoseCard
            prior={last}
            onConfirm={() => void repeatLast(last)}
            onDismiss={() => setRepeatDismissed(true)}
            disabled={busy}
          />
        ) : null}
        {shareSource ? (
          <div
            role="status"
            style={{
              background: 'var(--color-bg-sunken)',
              borderInlineStart: '3px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 'var(--text-caption)',
              color: 'var(--color-ink-secondary)',
            }}
          >
            Shared from <strong>{shareSource}</strong> — note prefilled below. Edit before saving if you want.
          </div>
        ) : null}
        <HALTPrompt
          onChange={(s) => void onHaltChange(s)}
          onDefer={() => onOpenChange(false)}
        />

        {/* Custom product registry (Gap 2, migration 0081). Quick-pick
            row above the form picker for one-tap logging of registered
            products like "Zana Chill 30mL = 15mg". Hidden when the
            user has no products yet to keep the sheet visually clean
            for new users. */}
        {products.length > 0 ? (
          <div className="log-dose-products">
            <div className="log-dose-products-label">Quick log</div>
            <div className="log-dose-products-chips">
              {products.map((p) => {
                const selected = productId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`log-dose-product-chip${selected ? ' selected' : ''}`}
                    onClick={() => (selected ? clearProduct() : applyProduct(p))}
                    title={p.notes ?? undefined}
                  >
                    <span className="log-dose-product-chip-label">{p.label}</span>
                    <span className="log-dose-product-chip-meta">
                      {p.amount} {p.amountUnit}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <FormChipMenu
          value={form}
          onChange={(f) => {
            setForm(f);
            // Clear product pin when the user changes form by hand —
            // the product was form-locked, so this is an explicit
            // override.
            if (productId != null) clearProduct();
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <NumberStepper
            value={amount}
            onChange={setAmountUserTouched}
            min={form === 'tablet_7oh' ? 1 : 0.1}
            max={form === 'tablet_7oh' ? 500 : form === 'leaf' ? 50 : 50}
            step={step}
            // Tablets: only the ±5 chip is on screen — ±10 lives in a
            // long-press on the central +/- (60% of dose adjustments are
            // ±5 or smaller; ±100 is rare enough that a preset chip
            // covers it). Leaf/extract keep the legacy ±5/±10 layout
            // until that flow gets its own polish pass.
            quickSteps={form === 'tablet_7oh' ? [5] : [5, 10]}
            holdJump={form === 'tablet_7oh' ? { multiplier: 10 } : undefined}
            hapticBoundaries={form === 'tablet_7oh' ? [5, 10, 30, 50, 100] : undefined}
            large
            suffix={unit}
            ariaLabel="dose amount"
          />
        </div>

        <MagnitudeAnchor anchor={anchor} value={computedMg} />

        {form === 'tablet_7oh' ? (
          <div className="scroll-strip">
            {COMMON_TABLET_STRENGTHS_MG.map((mg) => (
              <DosePresetChip key={mg} label={`${mg} mg`} onClick={() => setAmountUserTouched(mg)} />
            ))}
          </div>
        ) : null}

        {/* PR 3B (Gap 6) — brand picker. Optional; the server falls back
            to the label mg when no brand is picked. */}
        {form === 'tablet_7oh' ? (
          <details className="brand-picker-details">
            <summary className="brand-picker-summary">
              <span>
                {brandRow
                  ? `${brandRow.brand} · ${brandRow.productLabel}`
                  : 'Brand (optional, more honest mg)'}
              </span>
              <span className="dim text-caption">
                {brandRow ? '✓' : 'tap'}
              </span>
            </summary>
            <div style={{ marginBlockStart: 'var(--space-3)' }}>
              <BrandAutocomplete
                value={brandId}
                onChange={(id, row) => {
                  setBrandId(id);
                  setBrandRow(row);
                }}
              />
            </div>
          </details>
        ) : null}

        {form === 'extract' ? (
          <div className="field">
            <label className="field-label">Concentration (mg of 7-OH per mL)</label>
            <input
              className="field-control"
              type="number"
              inputMode="decimal"
              enterKeyHint="next"
              min={0.1}
              step={0.1}
              value={concentration}
              onChange={(e) => setConcentration(parseFloat(e.target.value) || 0)}
            />
          </div>
        ) : null}

        {form === 'leaf' ? (
          <div className="field">
            <label className="field-label">Strain (optional)</label>
            <input
              className="field-control"
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              enterKeyHint="next"
              value={strain}
              onChange={(e) => setStrain(e.target.value)}
              placeholder="e.g. Green Maeng Da"
            />
          </div>
        ) : null}

        {form === 'leaf' ? (
          <div className="field">
            <label className="field-label">Vendor (optional)</label>
            <input
              className="field-control"
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              enterKeyHint="next"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. Top Tree, Klarity, etc."
              maxLength={60}
            />
          </div>
        ) : null}

        {form === 'leaf' ? (
          <div className="field">
            <label className="field-label">Batch / lot (optional)</label>
            <input
              className="field-control"
              type="text"
              autoComplete="off"
              enterKeyHint="next"
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              placeholder="Bag ID, lot number, or date opened"
              maxLength={40}
            />
          </div>
        ) : null}

        <IntentChips value={intent} onChange={setIntent} />

        <TriggerChips value={tags} onChange={setTags} prioritized={suggestedTriggers} />

        <NoteField
          note={note}
          setNote={setNote}
          recording={recording}
          transcribing={transcribing}
          recordSeconds={recordSeconds}
          startRecording={startRecording}
          stopRecording={stopRecording}
          forceExpanded={shareSource != null || note.length > 0}
          expanded={noteExpanded}
          setExpanded={setNoteExpanded}
        />

        {/* Powder-tracking sprint (migration 0080). Flag the dose as a
            personal estimate rather than a lab-precise reading. Useful
            for unlabeled extract shots (e.g. Zana Chill), calibrated
            powder mg/g, or any time the mg number is your best guess.
            History UI renders flagged rows with a faint visual treatment
            so a 6-month-out audit can tell estimates apart from labels. */}
        <label className="field-checkbox-row">
          <input
            type="checkbox"
            checked={estimated}
            onChange={(e) => setEstimated(e.target.checked)}
          />
          <span>
            <strong>This is an estimate</strong>{' '}
            <span className="muted text-caption">
              — check when the mg is your best guess (unlabeled extract, calibrated powder, etc.) rather than a lab-precise label.
            </span>
          </span>
        </label>

        <div className="sheet-sticky-footer">
          <Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button>
          {logAnotherCount < 3 ? (
            <Button
              variant="secondary"
              size="md"
              onClick={onSaveAndAnother}
              disabled={busy || amount <= 0 || (form === 'extract' && concentration <= 0)}
              title={logAnotherCount > 0 ? `Logged ${logAnotherCount} in a row — one more then auto-close` : undefined}
            >
              Save &amp; log another
            </Button>
          ) : null}
          <Button
            onClick={onSave}
            loading={busy}
            // Block the obviously-invalid POST. Server still validates
            // (positive amount, positive concentration for extract), but
            // the round-trip is wasted UX. amount<=0 catches a user who
            // long-pressed past the min; concentration<=0 catches an
            // empty-on-blur extract input.
            disabled={amount <= 0 || (form === 'extract' && concentration <= 0)}
          >
            {editingDoseId != null ? 'Update' : 'Save'} · {formatComputedMg(computedMg)} mg of 7-OH
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------------
// Magnitude anchor: a single muted line under the stepper that frames
// the current value against the user's recent typical range and today's
// running total vs. the taper target. Hidden until the user has at
// least 5 logged doses — without history the line would be guesswork.
// ---------------------------------------------------------------------
interface AnchorData {
  todayMg: number;
  typical: { lo: number; hi: number } | null;
  targetMg: number | null;
}

function MagnitudeAnchor({ anchor, value }: { anchor: AnchorData | null; value: number }) {
  if (!anchor) return null;
  const { todayMg, typical, targetMg } = anchor;
  const projected = todayMg + value;
  const overTarget = targetMg != null && projected > targetMg;
  const underTarget = targetMg != null && projected <= targetMg;

  const parts: string[] = [];
  if (typical) {
    parts.push(`typical ${formatComputedMg(typical.lo)}–${formatComputedMg(typical.hi)} mg`);
  }
  if (targetMg != null) {
    parts.push(`${formatComputedMg(projected)} of ${formatComputedMg(targetMg)} mg today`);
  } else if (todayMg > 0) {
    parts.push(`${formatComputedMg(todayMg)} mg logged today`);
  }
  if (parts.length === 0) return null;

  // Tone shift: green when this dose keeps the user under today's
  // taper target; warning when it would push them over. Neutral if no
  // target is loaded yet. Honors prefers-reduced-motion implicitly —
  // it's a static color, not a transition.
  const color = overTarget
    ? 'var(--color-warning, var(--color-ink-secondary))'
    : underTarget
      ? 'var(--color-success, var(--color-ink-secondary))'
      : 'var(--color-ink-secondary)';

  return (
    <p
      className="text-caption"
      style={{
        margin: 0,
        textAlign: 'center',
        color,
        fontVariantNumeric: 'tabular-nums',
      }}
      aria-live="polite"
    >
      {parts.join(' · ')}
    </p>
  );
}

// Format a mg value with at most 1 fractional digit, dropping the
// trailing .0 so "7.0 mg" reads as "7 mg" — the typeface and the
// "mg of 7-OH" suffix do the formatting work.
function formatComputedMg(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (n === Math.round(n)) return String(Math.round(n));
  return n.toFixed(1);
}

// Linear-interpolation percentile on a pre-sorted array. p in [0, 1].
// Tolerant to short arrays — returns the only value when length=1.
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

// Time-of-day → 3 most-likely-relevant trigger tags. Pure heuristic
// (not personalized to the user). Used to re-order the chip picker so
// the highest-prior options sit on the top row at full size.
function suggestTriggersForHour(hour: number): TriggerTag[] {
  if (hour < 9) return ['withdrawal', 'sleep', 'craving'];
  if (hour < 14) return ['stress', 'pain', 'habit'];
  if (hour < 19) return ['stress', 'social', 'boredom'];
  return ['craving', 'habit', 'sleep'];
}

// ---------------------------------------------------------------------
// TimestampPill — backdate affordance for retroactive logs.
//
// Real users dose, then remember to log 30 minutes later. They
// shouldn't have to lie to the system or skip the log entirely. This
// pill sits at the top of the sheet showing the chosen moment ("Now",
// "30m ago", "1h ago", or a formatted local time when custom). Tap to
// expand a row of relative-time options + a "Custom…" reveal that
// drops in a native datetime-local input. Capped at 7 days back since
// older edits should go through the dose-list edit affordance, not
// the log sheet.
//
// API contract: server's POST /api/doses already accepts an optional
// `ts: ISO string` (DoseBody in functions-utils/validation.ts). The
// HALT-linkage SQL in functions/api/doses/index.ts uses the dose's ts
// (not server-now) so backdated doses still attribute correctly.
// ---------------------------------------------------------------------
const RELATIVE_OPTIONS: { label: string; minutesAgo: number }[] = [
  { label: '15m ago', minutesAgo: 15 },
  { label: '30m ago', minutesAgo: 30 },
  { label: '1h ago', minutesAgo: 60 },
  { label: '2h ago', minutesAgo: 120 },
  { label: '4h ago', minutesAgo: 240 },
];

function TimestampPill({
  value,
  onChange,
}: {
  value: Date | null;
  onChange: (next: Date | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [customMode, setCustomMode] = useState(false);

  const label = value ? formatRelativeOrTime(value) : 'Now';

  function pickRelative(minutesAgo: number) {
    onChange(new Date(Date.now() - minutesAgo * 60_000));
    haptics.confirm();
    setExpanded(false);
    setCustomMode(false);
  }
  function pickNow() {
    onChange(null);
    haptics.confirm();
    setExpanded(false);
    setCustomMode(false);
  }
  function pickCustom(iso: string) {
    if (!iso) return;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return;
    if (dt.getTime() > Date.now()) return; // no future doses
    onChange(dt);
    haptics.confirm();
    setExpanded(false);
    setCustomMode(false);
  }

  if (!expanded) {
    return (
      <button
        type="button"
        className="ts-pill"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
      >
        <Icon icon={Clock} size={14} />
        <span>{label}</span>
        <span className="ts-pill-chevron">▾</span>
      </button>
    );
  }

  // Build the datetime-local max value (now, in the user's local TZ
  // formatted YYYY-MM-DDTHH:mm).
  const nowMax = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  return (
    <div className="ts-pill-expanded" role="group" aria-label="Dose time">
      <button type="button" className={`ts-option${value === null ? ' ts-option-active' : ''}`} onClick={pickNow}>Now</button>
      {RELATIVE_OPTIONS.map((opt) => (
        <button
          key={opt.minutesAgo}
          type="button"
          className="ts-option"
          onClick={() => pickRelative(opt.minutesAgo)}
        >
          {opt.label}
        </button>
      ))}
      {!customMode ? (
        <button type="button" className="ts-option ts-option-custom" onClick={() => setCustomMode(true)}>
          Custom…
        </button>
      ) : (
        <input
          type="datetime-local"
          className="ts-custom-input"
          max={nowMax}
          autoFocus
          aria-label="Custom dose timestamp"
          onChange={(e) => pickCustom(e.target.value)}
        />
      )}
      <button
        type="button"
        className="ts-collapse"
        onClick={() => { setExpanded(false); setCustomMode(false); }}
        aria-label="Collapse time picker"
      >
        ✕
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------
// EditRecentBanner — when the user opens the sheet within 10 minutes
// of saving a dose of the same form, offer to edit that dose instead
// of logging a new one. Most common path: user typo'd "75 mg" instead
// of "7.5 mg" and immediately reopens to fix it.
// ---------------------------------------------------------------------
function EditRecentBanner({
  doseTs,
  amount,
  unit,
  onEdit,
  onDismiss,
}: {
  doseTs: string;
  amount: number;
  unit: string;
  onEdit: () => void;
  onDismiss: () => void;
}) {
  const minutesAgo = Math.max(1, Math.round((Date.now() - new Date(doseTs).getTime()) / 60_000));
  return (
    <div className="edit-recent-banner" role="status">
      <div className="edit-recent-banner-line">
        You logged <strong>{amount} {unit}</strong> {minutesAgo} minute{minutesAgo === 1 ? '' : 's'} ago.
      </div>
      <div className="edit-recent-banner-buttons">
        <button type="button" className="feelcheck-btn" onClick={onEdit}>Edit that one</button>
        <button type="button" className="feelcheck-skip" onClick={onDismiss}>Log new instead</button>
      </div>
    </div>
  );
}

// Whether an ISO timestamp is within the last `hours` hours. Used by
// the same-as-last hero card to suppress itself once the prior dose is
// stale (probably a different pattern day).
function isRecent(iso: string, hours: number): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < hours * 3600_000;
}

// Form name + unit label for the chip menu button.
const FORM_LABELS: Record<Form, string> = {
  tablet_7oh: 'Tablet · mg',
  leaf: 'Leaf · g',
  extract: 'Extract · mL',
};

// ---------------------------------------------------------------------
// IntentChips — single-select "why am I taking this right now?"
//
// Five options (matches DoseBody.intent enum). Optional — null means
// no declared intent. Renders as a single row above the trigger
// picker so the user encounters intent BEFORE the dose math, not
// after. Chips share the trigger-chip visual idiom for consistency.
// ---------------------------------------------------------------------
const INTENT_OPTIONS: { value: DoseIntent; label: string }[] = [
  { value: 'pain', label: 'Pain' },
  { value: 'stress', label: 'Stress' },
  { value: 'routine', label: 'Routine' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'unknown', label: 'Don’t know' },
];

function IntentChips({ value, onChange }: { value: DoseIntent | null; onChange: (next: DoseIntent | null) => void }) {
  function pick(v: DoseIntent) {
    if (value === v) {
      onChange(null);
      haptics.confirm();
      return;
    }
    onChange(v);
    haptics.confirm();
  }
  return (
    <div className="field">
      <span className="field-label">
        Why now? {value ? `· ${INTENT_OPTIONS.find((o) => o.value === value)?.label ?? ''}` : '(optional)'}
      </span>
      <div className="triggers-row" style={{ marginBlockStart: 'var(--space-2)' }}>
        {INTENT_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className="trigger-chip"
            data-on={value === o.value}
            aria-pressed={value === o.value}
            onClick={() => pick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// FormChipMenu — replaces the 3-segment SegmentedControl with a single
// chevron-menu chip. Saves a full row of vertical real estate for the
// 80%+ of saves on Tablet form, while keeping Leaf/Extract one tap
// away. Uses native <details> for zero-dependency dropdown behavior;
// keyboard accessibility comes for free (Enter on summary toggles,
// Tab through items).
// ---------------------------------------------------------------------
function FormChipMenu({ value, onChange }: { value: Form; onChange: (next: Form) => void }) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  function pick(next: Form) {
    onChange(next);
    if (detailsRef.current) detailsRef.current.open = false;
    haptics.confirm();
  }
  return (
    <details ref={detailsRef} className="form-chip-menu">
      <summary className="form-chip-menu-summary" aria-label={`Form: ${FORM_LABELS[value]}, change`}>
        <span>{FORM_LABELS[value]}</span>
        <span className="form-chip-menu-chevron">▾</span>
      </summary>
      <div className="form-chip-menu-list" role="menu">
        {(Object.keys(FORM_LABELS) as Form[]).map((f) => (
          <button
            key={f}
            type="button"
            role="menuitem"
            className={`form-chip-menu-item${value === f ? ' form-chip-menu-item-active' : ''}`}
            onClick={() => pick(f)}
          >
            {FORM_LABELS[f]}
          </button>
        ))}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------
// RepeatLastDoseCard — hero affordance for the most-common save: log
// the same dose as last time. Press-and-hold (600ms via existing
// useHoldToConfirm) to confirm; tap alone does nothing (prevents
// accidental double-logs). Dismissable for the rest of this open via
// the × button. Shown only when the prior dose is <12h old (older
// than that probably means a new pattern day, where suggesting a
// repeat would be misleading).
// ---------------------------------------------------------------------
function RepeatLastDoseCard({
  prior,
  onConfirm,
  onDismiss,
  disabled,
}: {
  prior: LastDose;
  onConfirm: () => void;
  onDismiss: () => void;
  disabled?: boolean;
}) {
  const hold = useHoldToConfirm({ durationMs: 600, onConfirm });
  const time = new Date(prior.ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const unit = prior.form === 'tablet_7oh' ? 'mg' : prior.form === 'leaf' ? 'g' : 'mL';
  const formLabel = prior.form === 'tablet_7oh' ? 'Tablet' : prior.form === 'leaf' ? 'Leaf' : 'Extract';
  return (
    <div className="repeat-card" data-holding={hold.holding} aria-label="Same as last dose">
      <button
        type="button"
        className="repeat-card-body"
        disabled={disabled}
        {...hold.bind}
        style={{ '--hold-progress': hold.progress } as React.CSSProperties}
      >
        <div className="repeat-card-line">
          <strong>Same as {time}</strong> — {formLabel}, {prior.amount}{prior.amount === Math.floor(prior.amount) ? '' : ''} {unit}
        </div>
        <div className="repeat-card-cta">{hold.holding ? 'Hold…' : 'Hold to log again'}</div>
      </button>
      <button
        type="button"
        className="repeat-card-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss same-as-last suggestion"
      >
        ✕
      </button>
    </div>
  );
}

// Friendly label for a Date relative to now: "5m ago", "1h ago",
// "Yesterday 8:14 PM", or just a localized time / date for older.
function formatRelativeOrTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 'Now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 12) return `${hours}h ago`;
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ---------------------------------------------------------------------
// NoteField: collapsed-by-default text+voice composer.
//
// Two changes from the original single-line input:
//   1. Collapsed default — when there's nothing to say, render a single
//      tap-to-expand row + an inline mic affordance. Most logs don't
//      have a note; the prior always-visible field added vertical
//      noise.
//   2. Textarea + don't-disable-on-record — switch from <input
//      type="text"> to <textarea rows={2}> so voice transcripts
//      (often a sentence or two) don't truncate visually, and let the
//      user keep typing while recording (the transcript is appended
//      on stop, not overwritten).
//
// Voice polish: while recording, a 3-bar CSS waveform pulses behind
// the mic button and a "0:23 / 1:00" countdown shows next to it.
// ---------------------------------------------------------------------
interface NoteFieldProps {
  note: string;
  setNote: (next: string | ((prev: string) => string)) => void;
  recording: boolean;
  transcribing: boolean;
  recordSeconds: number;
  startRecording: () => void;
  stopRecording: () => void;
  /** Force expanded (e.g. share-target prefill or returning user). */
  forceExpanded: boolean;
  expanded: boolean;
  setExpanded: (next: boolean) => void;
}

function NoteField({
  note,
  setNote,
  recording,
  transcribing,
  recordSeconds,
  startRecording,
  stopRecording,
  forceExpanded,
  expanded,
  setExpanded,
}: NoteFieldProps) {
  const isExpanded = expanded || forceExpanded || recording || transcribing;

  if (!isExpanded) {
    return (
      <div className="field note-field-collapsed-row">
        <button
          type="button"
          className="triggers-collapsed"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          <span className="triggers-collapsed-label">Note</span>
          <span className="triggers-collapsed-summary">· optional</span>
          <span className="triggers-collapsed-spacer" />
          <span className="triggers-collapsed-cta">+ Add</span>
          <Icon icon={Mic} size={16} />
        </button>
      </div>
    );
  }

  const remaining = Math.max(0, 60 - Math.floor(recordSeconds));
  const elapsed = Math.min(60, Math.floor(recordSeconds));

  return (
    <div className="field">
      <label className="field-label" htmlFor="note">Note (optional)</label>
      <div className="note-field-wrap">
        <textarea
          id="note"
          className="field-control note-textarea"
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="done"
          rows={2}
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          placeholder={transcribing ? 'Transcribing…' : recording ? 'Listening — keep typing if you want.' : 'Triggers, context…'}
        />
        <div className="note-mic-cluster">
          {recording ? (
            <span className="note-record-countdown" aria-live="polite">
              0:{String(elapsed).padStart(2, '0')} <span className="note-record-cap">/ 1:00</span>
            </span>
          ) : null}
          <button
            type="button"
            className="note-mic-btn"
            onClick={() => (recording ? stopRecording() : startRecording())}
            aria-label={recording ? `Stop recording (${remaining}s left)` : 'Record voice note'}
            disabled={transcribing}
            data-recording={recording}
          >
            {recording ? (
              <span className="note-waveform" aria-hidden="true">
                <span /><span /><span />
              </span>
            ) : (
              <Icon icon={Mic} size={18} />
            )}
          </button>
        </div>
      </div>
      <p className="dim text-caption" style={{ marginBlockStart: 'var(--space-1)' }}>
        {recording
          ? `Recording — tap stop when done. ${remaining}s left.`
          : transcribing
            ? 'Transcribing…'
            : 'Type or tap the mic to speak. Audio is never saved.'}
      </p>
    </div>
  );
}
